"use client";

import Image from "next/image";
import { TrendingDown } from "lucide-react";

type AlternativeCard = {
  title: string;
  price: string;
  description: string;
  badge: string;
  iconSrc: string;
};

type Benefit = {
  label: string;
  iconSrc: string;
};

const alternatives: AlternativeCard[] = [
  {
    title: "Creative Agency",
    price: "~$5000/Listing",
    description:
      "Requires logistics, shipping samples and month+ turnaround time.",
    badge: "Slow & Expensive",
    iconSrc: "/everything/Frame 99 (3).svg",
  },
  {
    title: "Graphic Designer",
    price: "~$2000/month",
    description:
      "Back and forth revisions and inconsistent style across the large SKUs",
    badge: "Manual Bottleneck",
    iconSrc: "/everything/Frame 99 (2).svg",
  },
];

const benefits: Benefit[] = [
  {
    label: "Instant Speed",
    iconSrc: "/everything/Frame 99 (4).svg",
  },
  {
    label: "Research Based Results",
    iconSrc: "/everything/Frame 99 (5).svg",
  },
];

function AlternativeCard({ item }: { item: AlternativeCard }) {
  return (
    <article className="flex min-h-[220px] w-full items-center rounded-[20px] border border-[#E0EAF4] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFFFFF_100%)] px-5 py-8 shadow-[0_4px_4px_rgba(7,0,107,0.04)] sm:min-h-[262px] sm:px-6">
      <div className="flex w-full items-start gap-3">
        <Image
          src={item.iconSrc}
          alt=""
          width={48}
          height={48}
          aria-hidden="true"
          className="h-12 w-12 shrink-0"
        />

        <div className="flex min-w-0 flex-1 flex-col items-start gap-3">
          <h3 className="text-[22px] font-semibold leading-[29px] text-[#1B1C30]">
            {item.title}
          </h3>
          <p className="text-[26px] font-semibold leading-[34px] text-[#1B1C30]">
            {item.price}
          </p>
          <p className="max-w-[372px] text-base leading-[21px] text-[#4D4D5C]">
            {item.description}
          </p>
          <span className="inline-flex h-7 items-center gap-1 rounded-full bg-[#EA545529] px-3 text-xs font-bold leading-4 text-[#EA5455]">
            <TrendingDown aria-hidden="true" className="h-5 w-5" />
            {item.badge}
          </span>
        </div>
      </div>
    </article>
  );
}

function BenefitRow({ item }: { item: Benefit }) {
  return (
    <div className="flex h-20 w-full items-center gap-3 rounded-[20px] border border-white px-4">
      <Image
        src={item.iconSrc}
        alt=""
        width={48}
        height={48}
        aria-hidden="true"
        className="h-12 w-12 shrink-0"
      />
      <h3 className="text-[20px] font-semibold leading-[27px] text-[#1B1C30] sm:text-[22px] sm:leading-[29px]">
        {item.label}
      </h3>
    </div>
  );
}

export default function PricingComparisonSection() {
  return (
    <section className="flex w-full flex-col items-center gap-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.5)_0%,rgba(207,230,255,0.5)_47.6%,rgba(255,255,255,0.5)_100%)] px-5 py-[60px] sm:px-10 lg:px-20 lg:pb-[120px]">
      <div className="flex w-full max-w-[1280px] flex-col items-center justify-center gap-[60px]">
        <div className="flex w-full flex-col items-center justify-center gap-4 text-center">
          <h2 className="max-w-[960px] text-[32px] font-bold leading-[42px] text-[#262626] sm:text-[40px] sm:leading-[52px]">
            Save 90% Compared to Traditional Methods
          </h2>
          <p className="text-base leading-[21px] text-[#262626]">
            See how our AI-powered solution dramatically reduces your costs while improving quality
          </p>
        </div>

        <div className="flex w-full flex-col items-stretch justify-between gap-6 lg:flex-row lg:gap-5 lg:px-20">
          <div className="mx-auto flex w-full max-w-[480px] flex-col gap-6 lg:mx-0">
            {alternatives.map((item) => (
              <AlternativeCard key={item.title} item={item} />
            ))}
          </div>

          <article className="mx-auto flex w-full max-w-[540px] flex-col items-center justify-center gap-10 rounded-[24px] border-x-2 border-y-[0.5px] border-white bg-[linear-gradient(180deg,rgba(167,197,255,0.08)_0%,rgba(0,57,167,0.08)_48.08%,rgba(167,197,255,0.08)_100%)] p-6 shadow-[0_8px_20px_rgba(17,0,255,0.08)] lg:mx-0">
            <div className="flex h-[52px] w-[53px] items-center justify-center">
              <Image
                src="/favicon.png"
                alt=""
                width={53}
                height={52}
                aria-hidden="true"
                className="h-[52px] w-[53px] object-contain"
              />
            </div>

            <div className="flex w-full flex-col items-center gap-3 text-center">
              <h3 className="text-[22px] font-semibold leading-[29px] text-[#1B1C30]">
                Speedy Listing
              </h3>
              <p className="text-[34px] font-semibold leading-[44px] text-[#1B1C30] sm:text-[38px] sm:leading-[50px]">
                ~$0.16/image
              </p>
              <p className="max-w-[352px] text-base leading-[21px] text-[#4D4D5C]">
                Instant generation, studio grade quality and infinite variants in one click
              </p>
            </div>

            <div className="flex w-full flex-col gap-4">
              {benefits.map((item) => (
                <BenefitRow key={item.label} item={item} />
              ))}
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
